import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@material-ui/core";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  PENDING_PIX_LINK_DEADLINE_MS,
  PENDING_PIX_VERIFY_WINDOW_MS,
} from "modules/payment/application/pix/pendingPixPolicy";
import { OrderLog } from "domains/order/agreggates/order/Order";
import {
  getPendingPixList,
  IPendingPix,
  subscribePendingPix,
} from "modules/payment/application/pix/pendingPixQueue";
import {
  runPendingPixNow,
  sendPendingPixNow,
} from "modules/payment/presentation/pendingPix/PendingPixVerifier";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GetError } from "utils/GetError";
import {
  buildPaymentHistory,
  IPaymentHistoryRow,
  matchesPaymentFilter,
  PaymentFilter,
} from "./paymentHistory";
import styles from "./PendingPixList.module.scss";

export const usePendingPixList = (): IPendingPix[] => {
  const [list, setList] = useState<IPendingPix[]>(() => getPendingPixList());

  useEffect(() => {
    setList(getPendingPixList());
    return subscribePendingPix(setList);
  }, []);

  return list;
};

const HISTORY_REFRESH_MS = 15_000;

/** Pagamentos aprovados do log do totem + fila de PIX; recarrega quando a fila muda e a cada 15 s */
export const usePaymentHistory = (): IPaymentHistoryRow[] => {
  const pixItems = usePendingPixList();
  const [orders, setOrders] = useState<OrderLog[]>([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const all = await getOrderRepository().all();
        if (active) setOrders(all);
      } catch (error) {
        GetError(error, "usePaymentHistory", "PendingPixList");
      }
    };
    load();
    const timer = setInterval(load, HISTORY_REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [pixItems]);

  return useMemo(() => buildPaymentHistory(orders, pixItems), [orders, pixItems]);
};

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const formatDate = (timestamp: number) => {
  const d = new Date(timestamp);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const formatValue = (value: number) =>
  `R$ ${value.toFixed(2).replace(".", ",")}`;

const statusLabel = (item: IPendingPix, now: number): string => {
  switch (item.status) {
    case "waiting":
      // Passou da janela sem nenhuma resposta real do servidor: operador confere
      if (item.lastCheckError && now - item.createdAt > PENDING_PIX_VERIFY_WINDOW_MS) {
        return `Sem confirmação — verificar (${item.checkAttempts} consultas)`;
      }
      return `Aguardando pagamento (${item.checkAttempts} consultas)`;
    case "sending":
      return "Enviando";
    case "sent":
      return "Pago e enviado";
    case "send_failed":
      return "Pago — falha no envio";
    case "expired":
      return "Expirado sem pagamento";
    default:
      return "";
  }
};

type StatusTone = "info" | "warning" | "success" | "danger" | "neutral";

const statusTone = (item: IPendingPix, now: number): StatusTone => {
  switch (item.status) {
    case "waiting":
      return item.lastCheckError && now - item.createdAt > PENDING_PIX_VERIFY_WINDOW_MS
        ? "warning"
        : "info";
    case "sending":
      return "warning";
    case "sent":
      return "success";
    case "send_failed":
      return "danger";
    default:
      return "neutral";
  }
};

const TONE_CLASS: Record<StatusTone, string> = {
  info: "toneInfo",
  warning: "toneWarning",
  success: "toneSuccess",
  danger: "toneDanger",
  neutral: "toneNeutral",
};

const summary = (rows: IPaymentHistoryRow[]) => {
  const pix = rows.filter((r) => r.pix).map((r) => r.pix as IPendingPix);
  const count = (statuses: IPendingPix["status"][]) =>
    pix.filter((it) => statuses.includes(it.status)).length;
  return [
    `${rows.length} ${rows.length === 1 ? "pagamento" : "pagamentos"}`,
    `${count(["waiting", "sending"])} PIX aguardando`,
    `${count(["send_failed"])} PIX com falha`,
  ].join(" · ");
};

const FILTERS: { key: PaymentFilter; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "pix", label: "PIX" },
  { key: "card", label: "Cartão" },
  { key: "cashless", label: "Cashless/Wallet" },
];

const DUPLICATE_WARNING =
  "Já passaram mais de 30 minutos desde o PIX. O servidor pode ter lançado este pagamento como venda avulsa e o envio pode duplicar. Enviar mesmo assim?";

export const PendingPixList = () => {
  const { kiosk } = useKiosk();
  const rows = usePaymentHistory();
  const [filter, setFilter] = useState<PaymentFilter>("all");
  const visibleRows = useMemo(
    () => rows.filter((row) => matchesPaymentFilter(row, filter)),
    [rows, filter]
  );
  const [verifying, setVerifying] = useState(false);
  const [sendingId, setSendingId] = useState<string | undefined>();
  const [confirmId, setConfirmId] = useState<string | undefined>();

  const verifyNow = useCallback(async () => {
    if (!kiosk) {
      return;
    }
    setVerifying(true);
    try {
      await runPendingPixNow(kiosk);
    } finally {
      setVerifying(false);
    }
  }, [kiosk]);

  const send = useCallback(
    async (pixId: string) => {
      if (!kiosk) {
        return;
      }
      setSendingId(pixId);
      try {
        await sendPendingPixNow(pixId, kiosk);
      } catch (error) {
        GetError(error, "send", "PendingPixList");
      } finally {
        setSendingId(undefined);
      }
    },
    [kiosk]
  );

  const onClickSend = useCallback(
    (item: IPendingPix) => {
      if (Date.now() - item.createdAt > PENDING_PIX_LINK_DEADLINE_MS) {
        setConfirmId(item.pixId);
        return;
      }
      send(item.pixId);
    },
    [send]
  );

  const confirmSend = useCallback(() => {
    const id = confirmId;
    setConfirmId(undefined);
    if (id) {
      send(id);
    }
  }, [confirmId, send]);

  return (
    <div id={styles.PendingPixList}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Pagamentos</h2>
          <span className={styles.summary}>{summary(rows)}</span>
        </div>
        <Button
          variant="outlined"
          className={styles.verifyButton}
          disabled={verifying || !kiosk}
          onClick={verifyNow}
        >
          {verifying ? "Verificando PIX..." : "Verificar PIX agora"}
        </Button>
      </div>
      <div className={styles.filters}>
        {FILTERS.map((it) => (
          <button
            key={it.key}
            type="button"
            className={`${styles.filter} ${filter === it.key ? styles.filterActive : ""}`}
            onClick={() => setFilter(it.key)}
          >
            {it.label}
          </button>
        ))}
      </div>
      <div className={styles.list}>
        {visibleRows.length === 0 && (
          <span className={styles.empty}>Nenhum pagamento neste filtro.</span>
        )}
        {visibleRows.map((row) => {
          const now = Date.now();
          const item = row.pix;
          return (
            <div
              className={styles.item}
              key={item ? item.pixId : row.id}
              data-testid={item ? "pending-pix-item" : "payment-item"}
            >
              <div className={styles.amount}>
                <span className={styles.value}>{formatValue(row.value)}</span>
                <span className={styles.kind}>{row.methodLabel}</span>
              </div>
              <div className={styles.info}>
                <span className={styles.payer}>{row.name ?? "Cliente não informado"}</span>
                <span className={styles.meta}>
                  {row.kindLabel}
                  {row.ref ? ` · Comanda/cartão ${row.ref}` : ""} · {formatDate(row.createdAt)}
                </span>
                {item && <span className={styles.pixId}>{item.pixId}</span>}
              </div>
              <div className={styles.side}>
                {item ? (
                  <span className={`${styles.status} ${styles[TONE_CLASS[statusTone(item, now)]]}`}>
                    {statusLabel(item, now)}
                  </span>
                ) : (
                  <span className={`${styles.status} ${styles.toneSuccess}`}>Aprovado</span>
                )}
                {item && item.status === "send_failed" && (
                  <>
                    {item.lastError && (
                      <span className={styles.error}>{item.lastError}</span>
                    )}
                    <Button
                      variant="contained"
                      className={styles.sendButton}
                      disabled={sendingId === item.pixId}
                      onClick={() => onClickSend(item)}
                    >
                      {sendingId === item.pixId ? "Enviando..." : "Enviar"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={!!confirmId} onClose={() => setConfirmId(undefined)}>
        <DialogContent>
          <DialogContentText>{DUPLICATE_WARNING}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(undefined)}>Cancelar</Button>
          <Button onClick={confirmSend}>Enviar mesmo assim</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
